/**
 * DEVLOGS-TEMP — boots the dev debug-log pipeline for the browser bundle.
 *
 * TEMPORARY, DEV ONLY. Fully inert unless `VITE_DEVLOG_URL` is baked into the
 * build, and refused outright on a production build. Remove with the rest of the
 * `DEVLOGS-TEMP` markers — see docs/dev-log-pipeline.md.
 *
 * `import.meta.env` is read HERE and passed down as a plain record rather than
 * being reached for inside the library: files under src/lib and src/player/lib
 * are also loaded by the Node test runner, where that Vite construct does not
 * exist. Same guarded-read reason as src/player/lib/client-report.ts.
 */

import { installWebDevLog } from "@workspace/devlog/web";
import { registerDevLogContext } from "@workspace/devlog";
import type { DevLogHandle } from "@workspace/devlog";
import { isDesktopShell } from "./platform";
import { isPlayerSurface } from "./player-surface";

let handle: DevLogHandle | null = null;

const PRODUCTION_HOSTS = new Set(["omnimedia.app", "www.omnimedia.app"]);

/**
 * True only on the real production origin. Deliberately a HOSTNAME check rather
 * than a build flag — see the note at the `isProduction` call site.
 */
function isProductionOrigin(): boolean {
  try {
    const host = (globalThis as { location?: { hostname?: string } }).location?.hostname;
    return typeof host === "string" && PRODUCTION_HOSTS.has(host.toLowerCase());
  } catch {
    // Unknowable (no location) => assume production and stay quiet.
    return true;
  }
}

function readEnv(): Record<string, string | undefined> {
  try {
    const env = import.meta.env as Record<string, string | undefined> | undefined;
    return env ?? {};
  } catch {
    return {};
  }
}

/**
 * Called from main.tsx after the API client is configured and BEFORE
 * observability initialises, so a failure inside Sentry/PostHog setup is itself
 * captured, and so our fetch wrapper sits under theirs rather than over it.
 */
export function initDevLog(): void {
  try {
    const env = readEnv();
    handle = installWebDevLog({
      env,
      surface: isDesktopShell() ? "desktop" : isPlayerSurface() ? "web-player" : "web-panel",
      // THE PRODUCTION REFUSAL, and it must key on the DEPLOYMENT, not the
      // build.
      //
      // `import.meta.env.MODE` / `.PROD` describe the BUILD TYPE. The dev
      // droplet serves a production-mode Vite build, so gating on them disabled
      // the pipeline on dev — exactly the same trap as NODE_ENV, which
      // compose.yml pins to "production" on both droplets.
      //
      // The honest runtime discriminator for this bundle is the origin it is
      // being served from. Production is omnimedia.app; everything else (the
      // dev droplet's sslip.io host, localhost, the Electron loopback) is not.
      isProduction: isProductionOrigin(),
      // Never log our own telemetry vendors' traffic back into the pipeline.
      ignoreHosts: ["eu.i.posthog.com", "de.sentry.io", "o4508.ingest.de.sentry.io"],
    });
  } catch {
    // A logger that cannot start must never stop the app from starting.
    handle = null;
  }
}

/** Identity is not known at boot — a player has no deviceId until it is paired. */
export function setDevLogDevice(context: {
  deviceId?: string;
  workspaceId?: string;
  locationId?: string;
  appVersion?: string;
}): void {
  try {
    registerDevLogContext(() => context);
  } catch {
    /* fire-and-forget */
  }
}

/** Counters for the player debug panel, so "no logs" is distinguishable from "no events". */
export function devLogStats(): {
  sent: number;
  failed: number;
  dropped: number;
  queued: number;
} | null {
  try {
    if (handle === null) return null;
    const stats = handle.logger.stats();
    return {
      sent: stats.sent,
      failed: stats.failed,
      dropped: stats.dropped,
      queued: stats.queued,
    };
  } catch {
    return null;
  }
}
